import { createSignal } from "solid-js";
import { BRIEF_STATUS } from "../../enums/brief";
import { canMarkBriefReady } from "../brief/briefReadiness";
import type { DeliveryBrief } from "../brief/briefTypes";
import { createGovernWork, reconcileGovernWork } from "./governModel";
import type { GovernWorkItem } from "./governTypes";

type GovernSessions = Record<string, GovernWorkItem[]>;

const [governSessions, setGovernSessions] = createSignal<GovernSessions>({});

export function governWorkItems(websiteId: string): GovernWorkItem[] {
  return governSessions()[websiteId] ?? [];
}

export function startGovernance(
  websiteId: string,
  brief: DeliveryBrief,
): GovernWorkItem {
  if (
    brief.status !== BRIEF_STATUS.READY_FOR_DELIVERY ||
    !canMarkBriefReady(brief)
  ) {
    throw new Error("Only a Ready for delivery Brief can enter Govern.");
  }
  const existing = governWorkItems(websiteId).find(
    (work) => work.sourceBriefId === brief.id,
  );
  const work = existing
    ? { ...reconcileGovernWork(existing, brief), openedAt: Date.now() }
    : createGovernWork(websiteId, brief);
  putGovernWork(websiteId, work);
  return work;
}

export function updateGovernWork(
  websiteId: string,
  workId: string,
  update: (work: GovernWorkItem) => GovernWorkItem,
): void {
  setGovernSessions((current) => ({
    ...current,
    [websiteId]: (current[websiteId] ?? []).map((work) =>
      work.id === workId ? update(work) : work,
    ),
  }));
}

export function syncGovernWorkSources(
  websiteId: string,
  briefs: DeliveryBrief[],
): void {
  const byId = new Map(briefs.map((brief) => [brief.id, brief]));
  const current = governWorkItems(websiteId);
  const next = current.map((work) =>
    reconcileGovernWork(work, byId.get(work.sourceBriefId) ?? null),
  );
  if (next.some((work, index) => work !== current[index])) {
    setGovernSessions((sessions) => ({ ...sessions, [websiteId]: next }));
  }
}

function putGovernWork(websiteId: string, work: GovernWorkItem): void {
  setGovernSessions((current) => {
    const existing = current[websiteId] ?? [];
    return {
      ...current,
      [websiteId]: [work, ...existing.filter((item) => item.id !== work.id)],
    };
  });
}
